import { memo, useContext } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import RelativeLink from "app/pages/.shared/RelativeLink";
import NewBadge from "app/pages/Header/conditionnalComponents/NewBadge";
import IconHelp from "app/pages/.shared/static/icons/IconHelp";

import "./AccountAsideMenuAuthenticated.scss";
import AmountContainer from "app/pages/.shared/AmountContainer";
import { brandPropTypes } from "app/utils/propTypes";
import { BRANDS } from "app/constants";
import DotNotification from "app/pages/.shared/DotNotification/DotNotification";
import AppGlobalsContext from "app/AppGlobalsContext";
import { IconCredit } from "app/pages/.shared/static/icons";
import AccountAsideMenuFooter from "app/pages/Account/AccountAsideMenu/AccountAsideMenuFooter";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import IconAccount from "app/pages/.shared/IconAccount/IconAccount";

const AccountAsideMenuAuthenticated = ({
	enableSponsorship,
	enableCredits,
	creditsAmount,
	creditNotesAmount,
	partnerCode,
	brand,
	shouldDisplayCreditNotesEntry,
	showCustomerRequestNotification,
	userIsConnected = false,
	logout = () => {},
	showSmartDPOnRootUrl = false,
}) => {
	const isBrandXX = brand === BRANDS.XX;

	const { shop } = useContext(AppGlobalsContext);

	return (
		<nav
			className="account-aside-menu-authenticated"
			data-testid="account-aside-menu-authenticated"
		>
			<ul className="account-aside-menu-authenticated__list">
				<li className="sub-nav">
					<div className="sub-nav__main">
						<div className="sub-nav__label">
							<IconAccount height={20} />
							<Typography variant={TYPOGRAPHY_VARIANTS.LARGE} isBold>
								<FormattedMessage id="menu.mobile.my.account" />
							</Typography>
						</div>
					</div>
					<ul className="sub-nav__list">
						{!isBrandXX && (
							<li className="sub-nav__item">
								<RelativeLink
									to={{ pathname: "/account/profile" }}
									activeClassName="sub-nav__item--active"
								>
									<FormattedMessage id="menu.item.my.profile" />
								</RelativeLink>
							</li>
						)}

						{enableCredits && (
							<li className="sub-nav__item sub-nav__item-with-chip">
								<RelativeLink
									to={{
										pathname: "/account/mycredits",
									}}
									activeClassName="sub-nav__item--active"
								>
									<FormattedMessage id="menu.item.my.credits" tagName="span" />
									{creditsAmount > 0 && (
										<div className="sub-nav__chip chip--credit">
											<IconCredit />
											<AmountContainer amount={creditsAmount} />
										</div>
									)}
								</RelativeLink>
							</li>
						)}

						{isBrandXX && (
							<li className="sub-nav__item">
								<RelativeLink
									to={{
										pathname: "/account/mybookings",
									}}
									activeClassName="sub-nav__item--active"
								>
									<FormattedMessage id="menu.item.my.bookings" />
								</RelativeLink>
							</li>
						)}

						{shouldDisplayCreditNotesEntry && (
							<li className="sub-nav__item sub-nav__item-with-chip">
								<RelativeLink
									to={{
										pathname: "/account/myrefunds",
									}}
									activeClassName="sub-nav__item--active"
								>
									<FormattedMessage id="menu.item.my.refunds" tagName="span" />
									{creditNotesAmount > 0 && (
										<div className="sub-nav__chip">
											<AmountContainer amount={creditNotesAmount} />
										</div>
									)}
								</RelativeLink>
							</li>
						)}

						{isBrandXX && (
							<li className="sub-nav__item">
								<RelativeLink
									rel="noopener noreferrer"
									target="_blank"
									to={{
										pathname:
											!creditNotesAmount && shop === "fr-FR"
												? `https://www.perfectstay.com/fr-FR`
												: "/listing",
									}}
								>
									<FormattedMessage id="menu.mobile.all.sales" />
								</RelativeLink>
							</li>
						)}

						{enableSponsorship && (
							<li className="sub-nav__item">
								<RelativeLink
									to={{
										pathname: "/account/sponsorship",
									}}
									activeClassName="sub-nav__item--active"
								>
									<FormattedMessage id="menu.item.sponsorship" />
									<NewBadge partnerCode={partnerCode} />
								</RelativeLink>
							</li>
						)}

						{!isBrandXX && (
							<li className="sub-nav__item">
								<RelativeLink
									to={{
										pathname: "/account/mybookings",
									}}
									activeClassName="sub-nav__item--active"
								>
									<FormattedMessage id="menu.item.my.bookings" />
								</RelativeLink>
							</li>
						)}

						{!isBrandXX && (
							<li className="sub-nav__item">
								<RelativeLink
									to={{
										pathname: "/account/subscriptions",
									}}
									activeClassName="sub-nav__item--active"
								>
									<FormattedMessage id="menu.item.my.subscriptions" />
								</RelativeLink>
							</li>
						)}

						{!isBrandXX && (
							<li className="sub-nav__item">
								<RelativeLink
									to={{
										pathname: "/account/security",
									}}
									activeClassName="sub-nav__item--active"
								>
									<FormattedMessage id="menu.item.security" />
								</RelativeLink>
							</li>
						)}

						{userIsConnected && (
							<li className="sub-nav__item">
								<RelativeLink
									to={{
										pathname: "/",
									}}
									onClick={() => logout()}
									activeClassName="sub-nav__item--active"
								>
									<FormattedMessage id="menu.item.logout" />
								</RelativeLink>
							</li>
						)}
					</ul>
				</li>
				<li className="sub-nav">
					<div className="sub-nav__main">
						<div className="sub-nav__label">
							<IconHelp height={20} />
							<Typography variant={TYPOGRAPHY_VARIANTS.LARGE} isBold>
								<FormattedMessage id="menu.item.need.help" />
							</Typography>
						</div>
					</div>
					<ul className="sub-nav__list">
						<li className="sub-nav__item">
							<RelativeLink
								to={{ pathname: "/account/faq-contact" }}
								activeClassName="sub-nav__item--active"
							>
								<FormattedMessage id="menu.item.faq.contact" />
							</RelativeLink>
						</li>
						<li className="sub-nav__item">
							<RelativeLink
								to={{ pathname: "/account/customer-request/my-requests" }}
								activeClassName="sub-nav__item--active"
							>
								<FormattedMessage id="menu.item.my.requests" />
								{showCustomerRequestNotification && <DotNotification />}
							</RelativeLink>
						</li>
					</ul>
				</li>
				<AccountAsideMenuFooter showSmartDPOnRootUrl={showSmartDPOnRootUrl} />
			</ul>
		</nav>
	);
};

AccountAsideMenuAuthenticated.propTypes = {
	enableSponsorship: PropTypes.bool,
	showCustomerRequestNotification: PropTypes.bool,
	shouldDisplayCreditNotesEntry: PropTypes.bool,
	userIsConnected: PropTypes.bool,
	showSmartDPOnRootUrl: PropTypes.bool,
	enableCredits: PropTypes.bool,
	partnerCode: PropTypes.string,
	creditsAmount: PropTypes.number,
	creditNotesAmount: PropTypes.number,
	brand: brandPropTypes,
	logout: PropTypes.func,
};

export default memo(AccountAsideMenuAuthenticated);
